import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const navLinks = [
    { label: t('nav_home'), href: '/' },
    { label: t('nav_products'), href: '/products' },
    { label: t('nav_about'), href: '/about' },
    { label: t('nav_contact'), href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer">
          <div className={`flex items-center justify-center transition-all duration-300 ${scrolled ? 'bg-[#C8714A] px-3 py-1.5 rounded-full' : ''}`}>
            <img
              src="https://storage.readdy-site.link/project_files/d60e0add-d934-490f-b5d8-9398c2931d82/d3cef690-9814-4ee5-8d70-6302dcf14eac_2.png"
              alt="Koorpet"
              className="h-9 w-auto object-contain"
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                location.pathname === link.href
                  ? scrolled ? 'text-[#C8714A]' : 'text-[#f5c9a8]'
                  : scrolled ? 'text-[#444] hover:text-[#C8714A]' : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA Button + Language */}
        <div className="hidden md:flex items-center gap-2">
          <LanguageSwitcher scrolled={scrolled} />
          <Link
            to="/products"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'bg-[#C8714A] text-white hover:bg-[#b5603b]'
                : 'bg-white text-[#1a1a1a] hover:bg-white/90'
            }`}
          >
            {t('nav_explore')}
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${scrolled ? 'text-[#1a1a1a]' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setMenuOpen(false)}
              className="text-sm font-medium text-[#444] hover:text-[#C8714A] cursor-pointer"
            >
              {link.label}
            </Link>
          ))}
          <div className="mt-2 pt-2 border-t border-gray-100">
            <LanguageSwitcher scrolled={true} />
          </div>
          <Link
            to="/products"
            onClick={() => setMenuOpen(false)}
            className="mt-2 px-5 py-2 rounded-full text-sm font-semibold bg-[#C8714A] text-white text-center cursor-pointer whitespace-nowrap"
          >
            {t('nav_explore')}
          </Link>
        </div>
      )}
    </header>
  );
}
